import type { TFunction } from 'i18next';
import { z } from 'zod';

import type { ADDRESS_FIELDS } from '../fields/collections/address.fields';
import type { AFFILIATION_FIELDS } from '../fields/collections/affiliation.fields';
import {
	LICENSING_AUTHORITY_MAX_LENGTH,
	type LICENSE_FIELDS
} from '../fields/collections/license.fields';

import {
	type IsUnavailable,
	maxLengthText,
	optionalBoolean,
	optionId,
	relaxUnavailable,
	requiredOptionId,
	requiredText,
	trimmed
} from './primitives';

type AddressShapeKey =
	keyof typeof ADDRESS_FIELDS | 'phoneCountryIso' | 'faxCountryIso';
type AffiliationShapeKey = keyof typeof AFFILIATION_FIELDS | 'parentHco';
type LicenseShapeKey = keyof typeof LICENSE_FIELDS;

const addressRequiredShape = (t?: TFunction) => ({
	streetAddress1Cda_V: requiredText(t),
	cityCda_V: requiredText(t)
});

export const addressRequiredSchema = (t?: TFunction) =>
	z.object(addressRequiredShape(t));

export const addressItemSchema = (
	t?: TFunction,
	isUnavailable?: IsUnavailable
) =>
	z.object(
		relaxUnavailable(
			{
				...addressRequiredShape(t),
				streetAddress2Cda_V: trimmed(),
				stateCda_VId: optionId(),
				postalCodeCda_V: trimmed(),
				postalCodeFullUs_V: trimmed(),
				countryCda_VId: optionId(),
				subStateAdminUnit_V: trimmed(),
				phoneCda_V: trimmed(),
				phoneCountryIso: optionId(),
				faxCda_V: trimmed(),
				faxCountryIso: optionId(),
				statusCda_VId: optionId(),
				primaryCda_V: optionalBoolean(),
				cbsa_VId: optionId(),
				subAdministrativeArea_V: trimmed(),
				phone1_V: trimmed(),
				fax1_V: trimmed()
			} satisfies Record<AddressShapeKey, z.ZodType>,
			isUnavailable
		)
	);

export const parentHcoDisplaySchema = z
	.object({
		name: z.string(),
		hcoType: z.string().nullish(),
		location: z.string().nullish()
	})
	.nullish();

export const affiliationItemSchema = (
	t?: TFunction,
	isUnavailable?: IsUnavailable
) =>
	z.object(
		relaxUnavailable(
			{
				parentHcoVid_V: requiredOptionId(t),
				parentHco: parentHcoDisplaySchema,
				typeOfRelationship_VId: optionId(),
				parentHcoStatus_VId: optionId(),
				isPrimaryRelationship_V: optionalBoolean(),
				jobTitle_V: trimmed(),
				role_V: trimmed(),
				relationshipType_VId: optionId()
			} satisfies Record<AffiliationShapeKey, z.ZodType>,
			isUnavailable
		)
	);

export const licenseItemSchema = (
	t?: TFunction,
	{
		requireLicenseNumber = false,
		isUnavailable
	}: { requireLicenseNumber?: boolean; isUnavailable?: IsUnavailable } = {}
) =>
	z.object(
		relaxUnavailable(
			{
				licenseNumber_V: requireLicenseNumber ? requiredText(t) : trimmed(),
				typeValue_V: maxLengthText(LICENSING_AUTHORITY_MAX_LENGTH, t),
				expirationDate_V: trimmed(),
				licenseStatus_VId: optionId()
			} satisfies Record<LicenseShapeKey, z.ZodType>,
			isUnavailable
		)
	);

export type AddressItemValues = z.input<ReturnType<typeof addressItemSchema>>;
export type AffiliationItemValues = z.input<
	ReturnType<typeof affiliationItemSchema>
>;
export type LicenseItemValues = z.input<ReturnType<typeof licenseItemSchema>>;
export type ParentHcoDisplay = NonNullable<
	z.input<typeof parentHcoDisplaySchema>
>;
